import React from "react";
import { cn } from "../lib/utils";
import { OriginBadge } from "../lib/origin.jsx";

export function Tile({ className, children, title, action, dense = false, testid, origin }) {
  return (
    <div data-testid={testid} className={cn("tile relative overflow-hidden", className)}>
      {(title || action || origin) && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            {typeof title === "string" ? (
              <span className="text-[11px] font-semibold tracking-widest uppercase text-muted">{title}</span>
            ) : (
              title
            )}
          </div>
          {(action || origin) && (
            <div className="flex items-center gap-2">
              {action}
              {origin && <OriginBadge {...origin} testid={testid ? `${testid}-origin` : undefined} />}
            </div>
          )}
        </div>
      )}
      <div className={cn(dense ? "" : "p-4")}>{children}</div>
    </div>
  );
}

export function StatusDot({ status = "info", className }) {
  const color = {
    success: "bg-success",
    warning: "bg-warning",
    critical: "bg-critical",
    info: "bg-info",
  }[status];
  return <span className={cn("inline-block w-2 h-2 rounded-full", color, className)} />;
}

export function Badge({ children, status = "info", className, testid }) {
  const colors = {
    success: "bg-success/10 text-success border-success/20",
    warning: "bg-warning/10 text-warning border-warning/30",
    critical: "bg-critical/10 text-critical border-critical/30",
    info: "bg-info/10 text-info border-info/30",
    neutral: "bg-surface-2 text-muted border-border",
  }[status];
  return (
    <span data-testid={testid} className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider border rounded-sm", colors, className)}>
      {children}
    </span>
  );
}

export function KPI({ label, value, sub, trend, status, testid, origin }) {
  const trendColor = trend > 0 ? "text-success" : trend < 0 ? "text-critical" : "text-muted";
  return (
    <div data-testid={testid} className="px-4 py-4 flex flex-col justify-between min-h-[110px]">
      <div className="kpi-label flex items-center gap-2">
        {status && <StatusDot status={status} />}
        {label}
        {origin && <OriginBadge {...origin} className="ml-auto" testid={testid ? `${testid}-origin` : undefined} />}
      </div>
      <div className="kpi-value text-3xl md:text-[34px] leading-none mt-3">{value}</div>
      {(sub || trend !== undefined) && (
        <div className="mt-2 flex items-center justify-between text-xs">
          <span className="text-muted font-mono-tabular">{sub}</span>
          {trend !== undefined && trend !== null && (
            <span className={cn("font-mono-tabular font-semibold", trendColor)}>
              {trend > 0 ? "▲" : trend < 0 ? "▼" : "•"} {Math.abs(trend).toFixed(1)}%
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function Sparkline({ data, color = "var(--primary)", height = 32, fill = true }) {
  if (!data || data.length === 0) return null;
  const w = 100;
  const h = height;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  // Guard against single-point arrays — SVG step would be Infinity otherwise.
  const step = data.length > 1 ? w / (data.length - 1) : 0;
  const points = data.map((v, i) => `${i * step},${h - ((v - min) / range) * h}`).join(" ");
  const area = `0,${h} ${points} ${w},${h}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="none" style={{ height }}>
      {fill && <polygon points={area} fill={`rgb(${color} / 0.12)`} />}
      <polyline points={points} stroke={`rgb(${color})`} strokeWidth="1.6" fill="none" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function SectionHeader({ eyebrow, title, description, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-6 mb-5">
      <div>
        {eyebrow && <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary mb-2">{eyebrow}</div>}
        <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">{title}</h2>
        {description && <p className="text-sm text-muted mt-1.5 max-w-2xl leading-relaxed">{description}</p>}
      </div>
      {action}
    </div>
  );
}
